/**
 * M27 task-07 — Identity Providers (ADM-07/08/18; HUB-083). Admin config for
 * the DB-backed login-SSO providers (Google now; GitHub as an unconfigured
 * slot) and the Drive connector's OWN OAuth app (a separate store, §4 D-18).
 * Every card follows the §2.1 "params + Enabled checkbox" shape; the client
 * secret is WRITE-ONLY — responses carry only `hasClientSecret`, so the card
 * shows a presence indicator and the redirect/callback URIs + Drive scope are
 * read-only. A read-only identity-config panel (ADM-09) sits at the bottom.
 *
 * The cards are pure/controlled (props in, callbacks out) so the no-jsdom seam
 * tests can render them through `renderToStaticMarkup`.
 */
import { useEffect, useState } from 'react';
import type {
  ConnectorOauth,
  IdentityConfig,
  OauthProviderConfig,
} from '@verybigthings/trackframe-shared/schemas';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SecretField } from '@/components/SecretField';
import { hub, hubBaseUrl } from '@/lib/hub-client';

/** Fixed read-only Drive scope the connector OAuth app is stamped with (D-18). */
export const DRIVE_READONLY_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';

/** Login providers the registry allows. Microsoft (Entra ID) also needs a tenant (HUB-102). */
const LOGIN_PROVIDERS = [
  { id: 'google', title: 'Google', tenant: false },
  { id: 'github', title: 'GitHub', tenant: false },
  { id: 'microsoft', title: 'Microsoft Entra ID', tenant: true },
] as const;
type LoginProviderId = (typeof LOGIN_PROVIDERS)[number]['id'];

/**
 * Origin of every callback URL the cards display (exported seam). The hub's
 * OWN `HUB_PUBLIC_URL` (from /admin/identity-config) is what it puts in the
 * OAuth `redirect_uri`, so that is what an operator must register — the SPA's
 * hub origin is only a fallback for a hub with OAuth unconfigured. On a local
 * Caddy setup the two differ (SPA on tf.com, hub on api.tf.com).
 */
export function callbackBase(config: IdentityConfig | null): string {
  return config?.hubPublicUrl?.replace(/\/+$/, '') || hubBaseUrl;
}
function providerRedirectUri(base: string, provider: string): string {
  return `${base}/auth/oauth/${provider}/callback`;
}
function connectorCallbackUri(base: string): string {
  return `${base}/connectors/google-drive/callback`;
}

/** Read-only URI row (redirect / callback), copy-selectable. */
function ReadOnlyUri({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <code className="block break-all rounded-md border bg-muted px-2 py-1.5 font-mono text-xs text-muted-foreground">
        {value}
      </code>
      <p className="text-xs text-muted-foreground">Add this to the provider&apos;s authorized redirect URIs.</p>
    </div>
  );
}

export interface ProviderCardProps {
  title: string;
  providerId: string;
  redirectUri: string;
  configured: boolean;
  hasClientSecret: boolean;
  clientId: string;
  clientSecret: string;
  enabled: boolean;
  busy: boolean;
  /** Entra tenant field — rendered only when `onTenantId` is given (Microsoft). */
  tenantId?: string;
  onTenantId?: (v: string) => void;
  onClientId: (v: string) => void;
  onClientSecret: (v: string) => void;
  onEnabled: (v: boolean) => void;
  onSave: () => void;
  onDelete: () => void;
}

/** Login-SSO provider card (exported seam). */
export function ProviderCard(props: ProviderCardProps) {
  const {
    title,
    providerId,
    redirectUri,
    configured,
    hasClientSecret,
    clientId,
    clientSecret,
    enabled,
    busy,
    tenantId,
    onTenantId,
    onClientId,
    onClientSecret,
    onEnabled,
    onSave,
    onDelete,
  } = props;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <span className={configured ? 'text-xs font-medium text-emerald-600' : 'text-xs text-muted-foreground'}>
            {configured ? (enabled ? 'Enabled' : 'Configured (disabled)') : 'Not configured'}
          </span>
        </div>
        <CardDescription>Login SSO via {title}.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {onTenantId && (
          <div className="space-y-2">
            <Label htmlFor={`${providerId}-tenantId`}>Tenant ID</Label>
            <Input
              id={`${providerId}-tenantId`}
              value={tenantId ?? ''}
              onChange={(e) => onTenantId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000 or contoso.com"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              One tenant only (Directory ID or a verified domain). common / organizations are refused.
            </p>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor={`${providerId}-clientId`}>Client ID</Label>
          <Input
            id={`${providerId}-clientId`}
            value={clientId}
            onChange={(e) => onClientId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <SecretField
          id={`${providerId}-clientSecret`}
          label="Client secret"
          hasSecret={hasClientSecret}
          value={clientSecret}
          onChange={onClientSecret}
        />
        <ReadOnlyUri label="Redirect URI" value={redirectUri} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
          Enabled
        </label>
        <div className="flex items-center justify-between">
          <Button type="button" onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          {configured && (
            <Button type="button" variant="outline" onClick={onDelete} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export interface ConnectorCardProps {
  redirectUri: string;
  scope: string;
  configured: boolean;
  hasClientSecret: boolean;
  clientId: string;
  clientSecret: string;
  enabled: boolean;
  busy: boolean;
  onClientId: (v: string) => void;
  onClientSecret: (v: string) => void;
  onEnabled: (v: boolean) => void;
  onSave: () => void;
  onDelete: () => void;
}

/** Drive-connector OAuth-app card (exported seam) — own client id/secret. */
export function ConnectorCard(props: ConnectorCardProps) {
  const {
    redirectUri,
    scope,
    configured,
    hasClientSecret,
    clientId,
    clientSecret,
    enabled,
    busy,
    onClientId,
    onClientSecret,
    onEnabled,
    onSave,
    onDelete,
  } = props;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Google Drive connector</CardTitle>
          <span className={configured ? 'text-xs font-medium text-emerald-600' : 'text-xs text-muted-foreground'}>
            {configured ? (enabled ? 'Enabled' : 'Configured (disabled)') : 'Not configured'}
          </span>
        </div>
        <CardDescription>
          The OAuth app that lets users connect their own Drive (separate from login SSO).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="drive-clientId">Client ID</Label>
          <Input
            id="drive-clientId"
            value={clientId}
            onChange={(e) => onClientId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <SecretField
          id="drive-clientSecret"
          label="Client secret"
          hasSecret={hasClientSecret}
          value={clientSecret}
          onChange={onClientSecret}
        />
        <div className="space-y-1">
          <Label>Scope</Label>
          <code className="block break-all rounded-md border bg-muted px-2 py-1.5 font-mono text-xs text-muted-foreground">
            {scope}
          </code>
          <p className="text-xs text-muted-foreground">Read-only Drive access, stamped server-side.</p>
        </div>
        <ReadOnlyUri label="Callback URL" value={redirectUri} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
          Enabled
        </label>
        <div className="flex items-center justify-between">
          <Button type="button" onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          {configured && (
            <Button type="button" variant="outline" onClick={onDelete} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Read-only ADM-09 identity-posture facts (exported seam). */
export function IdentityConfigPanel({ config }: { config: IdentityConfig | null }) {
  if (!config) return null;
  const rows: [string, string][] = [
    ['JWT issuer', config.jwt?.issuer ?? '—'],
    ['JWT audience', config.jwt?.audience ?? '—'],
    ['Access token TTL', config.jwt ? `${config.jwt.accessTokenTtlSec}s` : '—'],
    ['Refresh token TTL', config.jwt ? `${config.jwt.refreshTokenTtlSec}s` : '—'],
    ['Hub public URL', config.hubPublicUrl ?? '—'],
    ['SSO cookie domain', config.sso?.cookieDomain ?? '—'],
    ['SSO return-to allowlist', config.sso ? config.sso.returnToAllowlist.join(', ') || '—' : '—'],
    ['Email provider', config.emailProvider],
    ['First-admin bootstrap', config.bootstrap.enabled ? (config.bootstrap.hasAdmin ? 'done' : 'pending') : 'off'],
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Identity configuration</CardTitle>
        <CardDescription>Read-only snapshot of the hub&apos;s identity posture.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <dt className="text-xs text-muted-foreground">{k}</dt>
              <dd className="break-all text-sm">{v}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

// --- container: owns all form state; the cards above are pure ---

interface ProviderFormState {
  clientId: string;
  clientSecret: string;
  enabled: boolean;
  /** Entra tenant — only the Microsoft card edits it; absent elsewhere. */
  tenantId?: string;
}
const EMPTY_FORM: ProviderFormState = { clientId: '', clientSecret: '', enabled: false };

/**
 * PUT body for a write-only secret (exported seam). The secret field is blank
 * on every load, so it is sent ONLY when the operator typed a replacement —
 * omitting it keeps the stored one, which is what makes an Enabled-only save
 * possible (F8).
 */
export function putBody(
  form: ProviderFormState,
): { clientId: string; clientSecret?: string; enabled: boolean; tenantId?: string } {
  return {
    clientId: form.clientId.trim(),
    enabled: form.enabled,
    ...(form.clientSecret ? { clientSecret: form.clientSecret } : {}),
    ...(form.tenantId !== undefined ? { tenantId: form.tenantId.trim() } : {}),
  };
}

export function IdentityProviders() {
  const [loginConfigs, setLoginConfigs] = useState<Record<string, OauthProviderConfig | null>>({});
  const [loginForms, setLoginForms] = useState<Record<string, ProviderFormState>>({});
  const [connector, setConnector] = useState<ConnectorOauth | null>(null);
  const [connectorForm, setConnectorForm] = useState<ProviderFormState>(EMPTY_FORM);
  const [identityConfig, setIdentityConfig] = useState<IdentityConfig | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [providers, cfg] = await Promise.all([hub.identityProvidersList(), hub.identityConfigGet()]);
        const byProvider = Object.fromEntries(providers.map((p) => [p.provider, p]));
        const configs: Record<string, OauthProviderConfig | null> = {};
        const forms: Record<string, ProviderFormState> = {};
        for (const { id, tenant } of LOGIN_PROVIDERS) {
          const row = byProvider[id] ?? null;
          configs[id] = row;
          const base = row ? { clientId: row.clientId, clientSecret: '', enabled: row.enabled } : { ...EMPTY_FORM };
          forms[id] = tenant ? { ...base, tenantId: row?.tenantId ?? '' } : base;
        }
        setLoginConfigs(configs);
        setLoginForms(forms);
        setIdentityConfig(cfg);
      } catch (err) {
        setError((err as Error).message);
      }
      // The connector OAuth app is a separate store — a 404 just means unconfigured.
      try {
        const row = await hub.connectorOauthGet({ params: { connector: 'google-drive' } });
        setConnector(row);
        setConnectorForm({ clientId: row.clientId, clientSecret: '', enabled: row.enabled });
      } catch (err) {
        if (!(err instanceof HubClientError && err.httpStatus === 404)) setError((err as Error).message);
      }
    })();
  }, []);

  const patchLoginForm = (id: string, patch: Partial<ProviderFormState>) =>
    setLoginForms((prev) => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_FORM), ...patch } }));

  const saveLogin = async (id: LoginProviderId) => {
    const form = loginForms[id] ?? EMPTY_FORM;
    setBusyKey(id);
    setError(null);
    try {
      const row = await hub.identityProviderPut({
        params: { provider: id },
        body: putBody(form),
      });
      setLoginConfigs((prev) => ({ ...prev, [id]: row }));
      patchLoginForm(id, { clientSecret: '' });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const deleteLogin = async (id: LoginProviderId) => {
    setBusyKey(id);
    setError(null);
    try {
      await hub.identityProviderDelete({ params: { provider: id } });
      setLoginConfigs((prev) => ({ ...prev, [id]: null }));
      setLoginForms((prev) => ({ ...prev, [id]: { ...EMPTY_FORM } }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const saveConnector = async () => {
    setBusyKey('drive');
    setError(null);
    try {
      const row = await hub.connectorOauthPut({
        params: { connector: 'google-drive' },
        body: putBody(connectorForm),
      });
      setConnector(row);
      setConnectorForm((prev) => ({ ...prev, clientSecret: '' }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const deleteConnector = async () => {
    setBusyKey('drive');
    setError(null);
    try {
      await hub.connectorOauthDelete({ params: { connector: 'google-drive' } });
      setConnector(null);
      setConnectorForm({ ...EMPTY_FORM });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const base = callbackBase(identityConfig);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Identity Providers</h1>
        <p className="text-sm text-muted-foreground">
          Login SSO and the Drive-connector OAuth app. Secrets are write-only and stored encrypted.
        </p>
      </div>
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {LOGIN_PROVIDERS.map(({ id, title, tenant }) => {
          const form = loginForms[id] ?? EMPTY_FORM;
          const config = loginConfigs[id] ?? null;
          return (
            <ProviderCard
              key={id}
              title={title}
              providerId={id}
              redirectUri={providerRedirectUri(base, id)}
              configured={config !== null}
              hasClientSecret={config?.hasClientSecret ?? false}
              clientId={form.clientId}
              clientSecret={form.clientSecret}
              enabled={form.enabled}
              busy={busyKey === id}
              tenantId={form.tenantId}
              onTenantId={tenant ? (v) => patchLoginForm(id, { tenantId: v }) : undefined}
              onClientId={(v) => patchLoginForm(id, { clientId: v })}
              onClientSecret={(v) => patchLoginForm(id, { clientSecret: v })}
              onEnabled={(v) => patchLoginForm(id, { enabled: v })}
              onSave={() => void saveLogin(id)}
              onDelete={() => void deleteLogin(id)}
            />
          );
        })}
        <ConnectorCard
          redirectUri={connectorCallbackUri(base)}
          scope={connector?.scope ?? DRIVE_READONLY_SCOPE}
          configured={connector !== null}
          hasClientSecret={connector?.hasClientSecret ?? false}
          clientId={connectorForm.clientId}
          clientSecret={connectorForm.clientSecret}
          enabled={connectorForm.enabled}
          busy={busyKey === 'drive'}
          onClientId={(v) => setConnectorForm((prev) => ({ ...prev, clientId: v }))}
          onClientSecret={(v) => setConnectorForm((prev) => ({ ...prev, clientSecret: v }))}
          onEnabled={(v) => setConnectorForm((prev) => ({ ...prev, enabled: v }))}
          onSave={() => void saveConnector()}
          onDelete={() => void deleteConnector()}
        />
      </div>
      <IdentityConfigPanel config={identityConfig} />
    </div>
  );
}
